import { pipe, ReadonlyArray } from "fp";
import React from "react";
import styled from "styled-components";

import * as Animation from "~/Animation";
import * as Color from "~/Color";
import * as Data from "~/Data";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Time } from "~/Time";
import * as Typography from "~/Typography";

import { Brusher as Brusher_, Brush as Brush_ } from "./Brusher";
import { Controller as Controller_ } from "./Controller";
import { Event as Event_ } from "./Event";
import {
  Marker as Marker_,
  Markers as Markers_,
  Milestone as Milestone_,
} from "./Marker";
import { Menu as Menu_ } from "./Menu";
import { Row as Row_, Rows as Rows_ } from "./Row";
import { Scroll as Scroll_ } from "./Scroll";
import { Section as Section_ } from "./Section";

export type Timeline = Time.Interval.Stopped & { readonly now?: Time.DateTime };
export function Timeline({
  title,
  Action,
  statusEvents,
  milestones = [
    Milestone_.toMilestone({ type: "Local", ...Time.Zone.local() }),
  ],
  children: [timeline, ...sections],
}: {
  readonly title: string;
  readonly Action?: Component;
  readonly statusEvents: readonly Log.Event.UniqueEvent[];
  readonly milestones?: readonly Milestone_.Timezone[];
  readonly children: readonly [
    Timeline.Remote,
    ...(readonly Timeline.Section.Definition[])
  ];
}) {
  const { events, target, containerWidth, Scroll, Brush } = Timeline.Scroll.use(
    statusEvents
  );
  const { rows, menus } = Timeline.Children.render(timeline, sections);

  return (
    <Timeline.Container>
      <Timeline.Header.Component Left={Action} title={title} events={events} />
      <Animation.FadeIn.Component>
        <Timeline.Container>
          <Scroll timeline={timeline}>
            <Timeline.Markers
              timeline={timeline}
              milestones={milestones}
              targetContainer={target}
            />
            {rows}
          </Scroll>
          <Timeline.Fade left />
          <Timeline.Fade right />
          <Timeline.Menu>{menus}</Timeline.Menu>
          <Brush
            timeline={timeline}
            milestones={milestones}
            containerWidth={containerWidth}
          />
        </Timeline.Container>
      </Animation.FadeIn.Component>
    </Timeline.Container>
  );
}

export namespace Timeline {
  export import Brush = Brush_;
  export import Brusher = Brusher_;
  export import Controller = Controller_;
  export import Event = Event_;
  export import Marker = Marker_;
  export import Markers = Markers_;
  export import Menu = Menu_;
  export import Milestone = Milestone_;
  export import Row = Row_;
  export import Rows = Rows_;
  export import Scroll = Scroll_;
  export import Section = Section_;

  export type Remote = Data.Remote<Timeline>;

  export const percentageFromDateTime = (timeline: Timeline) => ({
    dateTime,
  }: Time.DateTime): number => {
    const { duration } = Time.Interval.Stopped.toDuration(timeline);
    const percentage = (dateTime - timeline.interval.start.dateTime) / duration;
    return percentage < 0 ? 0 : percentage > 1 ? 1 : percentage;
  };

  export const percentageToDateTime = (timeline: Timeline) => ({
    dateTime,
  }: Time.DateTime): number => {
    const { duration } = Time.Interval.Stopped.toDuration(timeline);
    const percentage =
      (timeline.interval.stop.dateTime -
        (dateTime + Timeline.Markers.durationBetween().duration)) /
      duration;
    return percentage < 0 ? 0 : percentage > 1 ? 1 : percentage;
  };

  export const pixelsPerMillisecond = (): number =>
    750 / Time.Duration.hours(1).duration;

  export const pixelsTotal = (timeline: Timeline): number =>
    Time.Interval.Stopped.toDuration(timeline).duration *
    pixelsPerMillisecond();

  export const Container = styled.div`
    position: relative;
  `;

  export type Children = {
    readonly rows: readonly Component.Rendered[];
    readonly menus: readonly Component.Rendered[];
  };

  export namespace Children {
    export const empty = (): Children => ({ rows: [], menus: [] });
    export const render = (
      timeline: Remote,
      sections: readonly Section.Definition[]
    ): Children =>
      pipe(
        sections,
        ReadonlyArray.reduceWithIndex(empty(), (index, previous, section) =>
          pipe(section(timeline), ({ Menu, Rows }) => ({
            menus: [...previous.menus, <Menu key={index} />],
            rows: [...previous.rows, <Rows key={index} />],
          }))
        )
      );
  }

  export type Fade = { readonly left?: boolean; readonly right?: boolean };
  export function Fade(fade: Fade) {
    return <Fade.Container {...fade} />;
  }

  export namespace Fade {
    export const width = () => "2em";
    export const Container = styled.div<{
      readonly left?: boolean;
      readonly right?: boolean;
    }>`
      z-index: ${() => Menu.zIndex()};
      position: absolute;
      top: 0;
      bottom: ${() => `calc(${Scroll.height()} + ${Scroll.paddingBottom()})`};

      ${({ right = false }) => (right ? "right: 0;" : "")};
      ${({ left = false }) => (left ? `left: ${Timeline.Menu.width()};` : "")};

      width: ${() => width()};

      background: linear-gradient(
        ${({ right = false }) => (right ? 270 : 90)}deg,
        ${() => Color.white()} 0%,
        ${() => Color.white()} 10%,
        ${() => Color.clear()} 100%
      );
    `;
  }

  export namespace Header {
    export type HeaderProps = {
      readonly title: string;
      readonly events: Scroll_.Events;
      readonly Left?: Component;
    };

    export const Component: Component<HeaderProps> = ({
      title,
      events,
      Left,
    }) => {
      return (
        <Layout.Section.Header.Component
          Left={Left}
          Right={() => <Timeline.Controller {...events} />}
        >
          <Title title={title} />
        </Layout.Section.Header.Component>
      );
    };

    export const Title: Component<{ readonly title: string }> = ({ title }) => (
      <Typography.H2.Component>{title}</Typography.H2.Component>
    );
  }
}
